import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ProductReview {
  id: number;
  productId: number;
  authorName: string;
  authorEmail?: string;
  date: string;
  rating: number;
  comment: string;
  status?: string;
}

export interface SubmitReviewPayload {
  productId: number;
  name: string;
  email: string;
  rating: number;
  comment: string;
}

interface ReviewsState {
  reviewsByProductId: Record<number, ProductReview[]>;
  fetchLoadingByProductId: Record<number, boolean>;
  fetchErrorByProductId: Record<number, string | null>;
  submitLoading: boolean;
  submitError: string | null;
  submitSuccessMessage: string | null;
}

const initialState: ReviewsState = {
  reviewsByProductId: {},
  fetchLoadingByProductId: {},
  fetchErrorByProductId: {},
  submitLoading: false,
  submitError: null,
  submitSuccessMessage: null,
};

const reviewsSlice = createSlice({
  name: 'reviews',
  initialState,
  reducers: {
    fetchProductReviewsRequest(state, action: PayloadAction<number>) {
      const productId = action.payload;
      state.fetchLoadingByProductId[productId] = true;
      state.fetchErrorByProductId[productId] = null;
    },
    fetchProductReviewsSuccess(
      state,
      action: PayloadAction<{ productId: number; reviews: ProductReview[] }>,
    ) {
      const { productId, reviews } = action.payload;
      state.fetchLoadingByProductId[productId] = false;
      state.fetchErrorByProductId[productId] = null;
      state.reviewsByProductId[productId] = reviews;
    },
    fetchProductReviewsFailure(
      state,
      action: PayloadAction<{ productId: number; error: string }>,
    ) {
      const { productId, error } = action.payload;
      state.fetchLoadingByProductId[productId] = false;
      state.fetchErrorByProductId[productId] = error;
    },
    submitProductReviewRequest(state, _action: PayloadAction<SubmitReviewPayload>) {
      state.submitLoading = true;
      state.submitError = null;
      state.submitSuccessMessage = null;
    },
    submitProductReviewSuccess(state) {
      state.submitLoading = false;
      state.submitError = null;
      state.submitSuccessMessage = 'Thank you! Your review is awaiting approval.';
    },
    submitProductReviewFailure(state, action: PayloadAction<string>) {
      state.submitLoading = false;
      state.submitError = action.payload;
      state.submitSuccessMessage = null;
    },
    clearReviewSubmitStatus(state) {
      state.submitError = null;
      state.submitSuccessMessage = null;
    },
  },
});

export const {
  fetchProductReviewsRequest,
  fetchProductReviewsSuccess,
  fetchProductReviewsFailure,
  submitProductReviewRequest,
  submitProductReviewSuccess,
  submitProductReviewFailure,
  clearReviewSubmitStatus,
} = reviewsSlice.actions;

export const reviewsReducer = reviewsSlice.reducer;
export default reviewsSlice.reducer;
